import { CSSProperties } from 'react'
import styled, { css, DefaultTheme } from 'styled-components'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { Typography } from '@/components/ui/typography'
import { withBorder } from '@/theme/utils'

import {
  DROPDOWN_LIST_ITEM_PADDING_HORIZONTAL,
  dropdownListScrollbar,
  dropdownListStyles,
  getDropdownListItemStyles,
} from '../dropdown-list'
import {
  LeadingSlot as FieldLeadingSlot,
  getFieldBoxStyles,
  getInputState,
  INPUT_FONT_SIZE_MAP,
  INPUT_LINE_HEIGHT_MAP,
} from '../text-input/text-input.styled'
import { OPTION_DEPTH_INDENT } from './select.constants'
import { SelectSize } from './select.types'

// Note: `Select` deliberately reuses `TextInput`'s field chrome so the two
// controls are visually identical. The container/label/field/footer layout and
// the leading-icon slot are imported wholesale; only the trigger (a `<button>`
// instead of an `<input>`) and the dropdown are Select-specific.
export {
  Container,
  Divider,
  Field,
  Footer,
  Message,
} from '../text-input/text-input.styled'

// Note: per the Select design the icon + divider sit in a 24px box (the 1px
// divider overflows it) and the text follows after the trigger's own gap - so
// drop the slot's extra trailing padding and let the divider overflow too.
// Note: `height: auto` lets the field box's `aspect-ratio: 1 / 1` square the icon -
// otherwise Phosphor's default `height="1em"` (the 12px/14px font size) wins
// over the box's `svg` width rule and the icon renders 16x12 / 20x14.
export const LeadingSlot = styled(FieldLeadingSlot)(({ theme }) => ({
  '& svg': {
    height: 'auto',
  },
  marginRight: `-${theme.border.size['1']}`,
  paddingRight: 0,
}))

// Note: used by the dropdown surface (search input, option rows, labels). The
// trigger itself reuses `INPUT_FONT_SIZE_MAP` from `TextInput` for exact parity.
const fontSizeMap: Record<SelectSize, string> = {
  medium: '1.4rem',
  small: '1.2rem',
}

export const TriggerWrapper = styled.div({
  display: 'inline-flex',
  width: '100%',
})

// Note: shared box chrome for both trigger variants below — the plain `<button>`
// trigger and the `<label>`-wrapped searchable trigger. Both must look exactly
// like a `TextInput` box, so this borrows the shared `getFieldBoxStyles`
// (border, background, padding, min-height, hover/focus, icon sizing) and only
// adds the value text size/line-height so the two line up identically.
const triggerBoxStyles = ({
  theme,
  $hasBadges,
  $hasError,
  $isDisabled,
  $isReadOnly,
  $size,
  interactiveCursor,
}: {
  theme: DefaultTheme
  $hasBadges: boolean
  $hasError: boolean
  $isDisabled: boolean
  $isReadOnly: boolean
  $size: SelectSize
  interactiveCursor: CSSProperties['cursor']
}) => css`
  ${getFieldBoxStyles({
    interactiveCursor,
    size: $size,
    state: getInputState({
      hasError: $hasError,
      isDisabled: $isDisabled,
      isReadOnly: $isReadOnly,
    }),
    theme,
  })}

  color: ${theme.colors.primary800};
  font-family: inherit;
  font-size: ${INPUT_FONT_SIZE_MAP[$size]};
  gap: ${theme.space['8']};
  justify-content: space-between;
  line-height: ${INPUT_LINE_HEIGHT_MAP[$size]};
  outline: none;
  overflow: hidden;
  text-align: left;

  /* Note: badges mode - the pills get extra vertical breathing room per design */
  ${
    $hasBadges &&
    css`
      padding-bottom: ${withBorder(theme, 8)};
      padding-top: ${withBorder(theme, 8)};
    `
  }
`

// Note: the non-searchable trigger is a `<button>` — click to open, shows the
// selected label (or placeholder) as static text.
export const SelectButton = styled.button<{
  $hasBadges: boolean
  $hasError: boolean
  $isDisabled: boolean
  $isReadOnly: boolean
  $size: SelectSize
}>(({ theme, $hasBadges, $hasError, $isDisabled, $isReadOnly, $size }) =>
  triggerBoxStyles({
    $hasBadges,
    $hasError,
    $isDisabled,
    $isReadOnly,
    $size,
    interactiveCursor: 'pointer',
    theme,
  }),
)

// Note: the searchable trigger is a `<label>` wrapping a real `<input>` (same
// trick as `TextInput`'s `InputWrapper` — clicking anywhere in the box, not
// just the input itself, focuses the control) so typing happens directly in
// the field instead of a separate search box inside the opened dropdown.
export const SelectSearchBox = styled.label<{
  $hasBadges: boolean
  $hasError: boolean
  $isDisabled: boolean
  $isReadOnly: boolean
  $size: SelectSize
}>(({ theme, $hasBadges, $hasError, $isDisabled, $isReadOnly, $size }) =>
  triggerBoxStyles({
    $hasBadges,
    $hasError,
    $isDisabled,
    $isReadOnly,
    $size,
    interactiveCursor: 'text',
    theme,
  }),
)

export const SelectButtonContent = styled.div<{ $isMulti?: boolean }>(({ theme, $isMulti }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  flexWrap: 'nowrap',
  gap: $isMulti ? theme.space['4'] : theme.space['0'],
  minWidth: 0,
  overflow: 'hidden',
}))

// Note: the selected value reads as `primary800` (placeholder `primary500`); the
// error state reads `red500` (the placeholder lightens to `red400` while the box
// is hovered/focused). Disabled wins over error (a disabled field shows grey text
// even with an error message below) and shows value and placeholder alike in
// `primary400`.
export const SelectButtonText = styled(Typography)<{
  $isPlaceholder: boolean
  $hasError: boolean
  $isDisabled: boolean
  $size: SelectSize
}>(({ theme, $isPlaceholder, $hasError, $isDisabled, $size }) => {
  const errorColors = {
    placeholder: theme.colors.red500,
    value: theme.colors.red500,
  }
  const disabledColors = {
    placeholder: theme.colors.primary400,
    value: theme.colors.primary400,
  }
  const defaultColors = {
    placeholder: theme.colors.primary500,
    value: theme.colors.primary800,
  }
  const colors = $isDisabled ? disabledColors : $hasError ? errorColors : defaultColors

  const hasActiveErrorPlaceholder = $isPlaceholder && $hasError && !$isDisabled

  return {
    color: $isPlaceholder ? colors.placeholder : colors.value,
    fontSize: INPUT_FONT_SIZE_MAP[$size],
    lineHeight: INPUT_LINE_HEIGHT_MAP[$size],

    ...(hasActiveErrorPlaceholder && {
      [`${SelectButton}:focus-within &`]: { color: theme.colors.red400 },
      '@media (hover: hover)': {
        [`${SelectButton}:hover &`]: { color: theme.colors.red400 },
      },
    }),
  }
})

// Note: the dropdown caret reuses the shared `Button` with the `ghost` (or
// `ghost-error`) appearance — same hover/focus/active logic and colors as the
// `TextInput` trailing actions (`ghost` → `primary800`, `ghost-error` → `red500`,
// matching the previous caret colors). It is rendered `as="span"` to avoid
// nesting a `<button>` inside the trigger button and adds the Select-only
// padding scale plus the open/closed rotation. Icon size comes from the field
// box's `svg` rule on `SelectButton`.
export const IconWrapper = styled(Button)<{
  $isDisabled: boolean
  $isOpen: boolean
  $size: SelectSize
}>(({ theme, $isDisabled, $isOpen, $size }) => {
  const paddingMap: Record<SelectSize, CSSProperties['padding']> = {
    medium: `${theme.space['6']}`,
    small: `${theme.space['4']}`,
  }

  return {
    '& svg': {
      transform: $isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: `transform ${theme.transition.duration.sm} ease`,
    },
    ...($isDisabled && { color: theme.colors.primary300 }),
    flexShrink: 0,
    padding: `${paddingMap[$size]} !important`,
  }
})

export const Dropdown = styled.div(
  ({ theme }) => css`
    /* Note: the shared design-system dropdown panel. Its border-box sizing keeps the border
       part of the width, so the dropdown is exactly as wide as the trigger */
    ${dropdownListStyles};

    display: flex;
    flex-direction: column;
    max-height: 28rem;
    overflow: hidden;
    z-index: ${theme.zIndex.level3};
  `,
)

// Note: lives inside `SelectSearchBox` (the trigger), not the dropdown — reuses
// the trigger's own font metrics (`INPUT_FONT_SIZE_MAP`/`INPUT_LINE_HEIGHT_MAP`)
// rather than the dropdown's `fontSizeMap` so typed text matches the
// placeholder/selected-label text size exactly.
export const SearchTriggerInput = styled.input<{ $size: SelectSize; $hasError: boolean }>(
  ({ theme, $size, $hasError }) => css`
    background: transparent;
    border: none;
    color: ${$hasError ? theme.colors.red500 : theme.colors.primary800};
    flex: 1;
    font-family: inherit;
    font-size: ${INPUT_FONT_SIZE_MAP[$size]};
    line-height: ${INPUT_LINE_HEIGHT_MAP[$size]};
    min-width: 0;
    outline: none;
    padding: 0;
    text-align: left;

    &::placeholder {
      color: ${$hasError ? theme.colors.red500 : theme.colors.primary500};
    }

    /* Note: per design the error placeholder lightens to red400 while the box is hovered/focused */
    ${
      $hasError &&
      css`
        ${SelectSearchBox}:focus-within &:not(:disabled)::placeholder {
          color: ${theme.colors.red400};
        }

        @media (hover: hover) {
          ${SelectSearchBox}:hover &:not(:disabled)::placeholder {
            color: ${theme.colors.red400};
          }
        }
      `
    }

    &:disabled {
      color: ${theme.colors.primary400};
      cursor: not-allowed;

      &::placeholder {
        color: ${theme.colors.primary400};
      }
    }
  `,
)

export const DropdownLabel = styled.div<{ $isMenu?: boolean; $size: SelectSize }>(
  ({ theme, $isMenu, $size }) => ({
    borderBottom: `${theme.border.size['1']} solid ${
      $isMenu ? theme.colors.gray400 : theme.colors.gray300
    }`,
    color: theme.colors.black500,
    flexShrink: 0,
    fontSize: fontSizeMap[$size],
    fontWeight: 500,
    padding: $isMenu ? theme.space['12'] : `${theme.space['8']} ${theme.space['12']}`,
    ...($isMenu && { lineHeight: '1.6rem' }),
  }),
)

export const OptionsList = styled.div(
  () => css`
    ${dropdownListScrollbar};

    scrollbar-gutter: stable;
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
  `,
)

export const SelectOptionButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  fontSize: '1.2rem',
  height: '2.4rem',
  lineHeight: '1.6rem',
  marginLeft: 'auto',
  padding: `${theme.space['4']} ${theme.space['12']}`,
  visibility: 'hidden',
}))

export const Option = styled.div<{
  $isActive: boolean
  $isMenu: boolean
  $isDisabled: boolean
  $size: SelectSize
  $depth?: number
  $caretOffset: string
}>(({ theme, $isActive, $isMenu, $isDisabled, $size, $depth, $caretOffset }) => {
  // Note: the row itself is the design system's "Dropdown List Item" - only the tree indent and
  // the hover-revealed "Move" button are Select-specific.
  const itemStyles = getDropdownListItemStyles(theme, {
    isActive: $isActive,
    isDisabled: $isDisabled,
    size: $size,
  })

  return {
    ...itemStyles,

    [`&:hover ${SelectOptionButton}`]: {
      visibility: 'visible',
    },

    // Note: the shared disabled state only recolors text and background - dim the row's non-text
    // content (badges, counts) to match; its checkbox gets `disabled` in select-option-item.
    ...($isDisabled && {
      '[data-component-id="Badge"], [data-component-id="QuantityBadge"]': { opacity: 0.5 },
    }),

    height: '100%',
    paddingLeft: `calc(${$isMenu ? theme.space['12'] : DROPDOWN_LIST_ITEM_PADDING_HORIZONTAL} + ${$depth} * ${OPTION_DEPTH_INDENT} + ${$caretOffset})`,

    // Note: the 'menu' variant's "Modal menu item" - 6px 12px padding and an 8px gap.
    ...($isMenu && {
      gap: theme.space['8'],
      paddingBottom: '0.6rem',
      paddingRight: theme.space['12'],
      paddingTop: '0.6rem',
    }),
  }
})

export const DropdownLoader = styled(BorderLoader)(
  ({ theme }) => css`
    margin: ${theme.space['12']} auto;
  `,
)

export const NoOptionsMessage = styled.div<{ $size: SelectSize }>(({ theme, $size }) => ({
  color: theme.colors.black300,
  fontSize: fontSizeMap[$size],
  padding: `${theme.space['12']}`,
  textAlign: 'center',
}))

export const ResetButton = styled.button<{ $isMenu?: boolean; $size: SelectSize }>(
  ({ theme, $isMenu, $size }) => ({
    '&:hover': {
      backgroundColor: theme.colors.gray200,
    },
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    border: 'none',
    borderTop: `${theme.border.size['1']} solid ${theme.colors.gray300}`,
    color: theme.colors.black500,
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    fontSize: fontSizeMap[$size],
    gap: theme.space['8'],
    justifyContent: 'center',
    padding: `${theme.space['8']} ${theme.space['12']}`,
    transition: `background-color ${theme.transition.duration.sm} ease`,
    width: '100%',

    // Note: the 'menu' variant's reset is a regular "Modal menu item" row - left-aligned, primary
    // text, 32px tall with the 20px ArrowUUpLeft icon.
    ...($isMenu && {
      borderTopColor: theme.colors.gray400,
      color: theme.colors.primary800,
      justifyContent: 'flex-start',
      lineHeight: '1.6rem',
      minHeight: '3.2rem',
      padding: `0.6rem ${theme.space['12']}`,
    }),
  }),
)

export const VirtualListContainer = styled.div({
  position: 'relative',
  width: '100%',
})

export const VirtualItem = styled.div({
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
})

export const ExpandToggle = styled.button<{ $isExpanded?: boolean }>(({ theme, $isExpanded }) => ({
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: theme.border.radius['4'],
  color: theme.colors.black500,
  cursor: 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
  justifyContent: 'center',
  padding: theme.space['0'],
  transform: $isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
  transition: `transform ${theme.transition.duration.sm} ease`,
}))

export const CheckboxWrapper = styled.div<{ $interactive?: boolean }>(({ $interactive }) => ({
  alignItems: 'center',
  cursor: $interactive ? 'pointer' : undefined,
  display: 'flex',
  // Note: normally decorative (the row click handles toggling) - interactive only
  // for the tree-checkbox mode, where the checkbox is its own click target so a
  // parent's checkbox can toggle selection without also triggering expand/collapse.
  pointerEvents: $interactive ? 'auto' : 'none',
}))

export const TriggerStatusBadge = styled(Badge)(({ theme }) => ({
  flexShrink: 0,
  marginLeft: theme.space['8'],
}))

// Note: normal-flow flex-wrap (not an absolute overlay) so wrapped badges grow
// the trigger's height - it relies on the trigger box using `min-height`, not a
// fixed `height`. Mirrors the `TagsWrapper` badge-overflow pattern in
// general-information.styled.ts.
export const BadgesArea = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  flexWrap: 'wrap',
  gap: theme.space['4'],
  minWidth: 0,
}))

// Note: keeps the searchable trigger's real `<input>` in the DOM (so Tab/focus
// still opens the dropdown) while badges are showing, without it taking up flow
// space or blocking clicks on a wrapped badge's remove button.
export const HiddenSearchLayer = styled.div({
  inset: 0,
  opacity: 0,
  pointerEvents: 'none',
  position: 'absolute',
})

// Note: the caret + selected count, spaced tighter (space-4) than the trigger
// box's own gap. Centered across the trigger's full height, also when badges wrap.
export const TrailingGroup = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: theme.space['4'],
}))

export const OptionAction = styled(Button)({
  flexShrink: 0,
  marginLeft: 'auto',
})

export const OptionCountBadge = styled.div({
  flexShrink: 0,
  marginLeft: 'auto',
})

export const TriggerDivider = styled.div<{ $hasError: boolean }>(({ theme, $hasError }) => ({
  alignSelf: 'center',
  background: $hasError ? theme.colors.red300 : theme.colors.gray400,
  flexShrink: 0,
  height: '1.6rem',
  width: theme.border.size['1'],
}))
